import { Progress } from '@agro-club/agroclub-shared'
import Chatra from '@chatra/chatra'
import addChatraUserUnfo from 'modules/domain/auth/addChatraUserUnfo'
import { clearToken, getAccessToken } from 'modules/domain/auth/repository'
import { UserMe } from 'modules/domain/auth/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { createContext, useCallback, useEffect, useState } from 'react'
import { generatePath, useMatch, useNavigate } from 'react-router'
import { Sentry } from 'sentry'
import AuthRoutes from 'views/pages/Auth/routes'

interface AuthProps {
  isAuthenticated: boolean
  progress: Progress
  profile?: UserMe
  onLogin: () => Promise<void>
  logOut: () => Promise<void>
}

export const AuthContext = createContext<AuthProps>({
  isAuthenticated: false,
  progress: Progress.IDLE,
  onLogin: function (): Promise<void> {
    throw new Error('Function not implemented.')
  },
  logOut: function (): Promise<void> {
    throw new Error('Function not implemented.')
  },
})

export function AuthProvider({ children }: any) {
  const [profile, setProfile] = useState<UserMe>()
  const navigate = useNavigate()
  const isAuthenticated = !!profile
  const [progress, setProgress] = useState<Progress>(Progress.WORK)
  const isLoginPage = useMatch(AuthRoutes.Login)

  const onLogin = useCallback(async () => {
    setProgress(Progress.WORK)
    const profile = await apiClient.get<UserMe>(endpoints.profile())
    setProfile(profile)
    addChatraUserUnfo(profile)
    Sentry.sentryInstance?.setUser({ phone: profile.phone, id: profile.id })
    setProgress(Progress.SUCCESS)
  }, [])

  const logOut = useCallback(async () => {
    clearToken()
    setProfile(undefined)
    Chatra('kill')
    setProgress(Progress.IDLE)
    navigate(
      { pathname: generatePath(AuthRoutes.Login) },
      { state: { from: window.location.pathname + window.location.search } },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const accessToken = getAccessToken()
    if (accessToken) {
      onLogin()
    } else {
      setProgress(Progress.IDLE)
      if (!isLoginPage) {
        // if we are not on login page, redirect to login
        navigate(
          { pathname: generatePath(AuthRoutes.Login) },
          { state: { from: window.location.pathname + window.location.search } },
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLogin])

  return (
    <AuthContext.Provider value={{ isAuthenticated, profile, onLogin, logOut, progress }}>
      {children}
    </AuthContext.Provider>
  )
}
